import { getAnalyticsConfig } from './analytics-status'

/**
 * An object's two statuses, and why they differ.
 *
 *   scan      — the object's OWN latest scan.
 *   hierarchy — rolled up, and only ever GREEN, AMBER or GREY (Andrew, 2026-09-14). Red is a
 *               statement about an object's own copies, so it never appears here: a confirmed
 *               detection, on this object or beneath it, makes the roll-up AMBER. Amber (3/5)
 *               does not roll up at all (Andrew, 2026-09-10: "treat amber as green for the roll
 *               up") — a dependency with indicators leaves the colour alone and is flagged
 *               beside it, so a green object over one shows GREEN plus an amber triangle.
 *
 * Decisions about an object's own copies (retrieve green, red counts) use `scan`, never
 * `hierarchy`: an object amber only because of something beneath it has nothing wrong with its
 * own recovery points. Wording everywhere comes from here so the canvas, the tables and the
 * object page cannot drift into three explanations of one status.
 */
export interface StatusPair {
    scan: number | null | undefined
    /** The roll-up as the view says it may be drawn (HierarchyDisplay): never red. */
    hierarchy: number | null | undefined
    /** The confirmed detection is on THIS object, not beneath it — why an amber has no red count. */
    redHere?: number | null
    redBelow?: number | null
    indicatorsBelow?: number | null
    redBelowIds?: string[] | null
    indicatorsBelowIds?: string[] | null
}

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string

/** Fallback when no i18n `t` is to hand: fills {{placeholders}} in the English default. */
const english: Translate = (_key, fallback, opts) =>
    fallback.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => String(opts?.[name] ?? ''))

/** Names a few, and says how many more — a tooltip is not the place for the whole estate. */
function named(ids: string[] | null | undefined, count: number): string {
    const list = (ids || []).slice(0, 5)
    if (list.length === 0) return ''
    const more = count - list.length
    return ` (${list.join(', ')}${more > 0 ? `, +${more}` : ''})`
}

/**
 * The roll-up as it is allowed to be shown: green, amber or grey. A 2 from an older API (or a
 * database whose view has not been replaced yet) is drawn as amber, never as red.
 */
export function rollupCode(code: number | null | undefined): number | null {
    if (code == null) return null
    const n = Number(code)
    return n === 2 ? 3 : n
}

/** Is the roll-up already amber, so a second amber mark beside it would only repeat it? */
export function rollupIsAmber(p: StatusPair): boolean {
    const n = rollupCode(p.hierarchy)
    return n === 3 || n === 5
}

/** Does the hierarchy say something the scan does not? */
export function hierarchyDiffers(p: StatusPair): boolean {
    return p.hierarchy != null && p.scan != null && rollupCode(p.hierarchy) !== Number(p.scan)
}

/**
 * The hierarchy's label. Code 3's own label says "Indicators detected", which describes an
 * object's OWN heuristic findings — but a roll-up is 3 because something RED sits in the stack,
 * and calling that "indicators" would be wrong twice over. Name the reason instead: a dependency
 * beneath, or this object's own confirmed detection, which the roll-up shows as amber.
 */
export function hierarchyLabel(p: StatusPair, t: Translate = english): string {
    // redHere comes from the view; the scan is the fallback for a payload that predates it.
    if (rollupIsAmber(p) && (Number(p.redHere || 0) > 0 || Number(p.scan) === 2)) {
        return t('objects.statusPair.amberFromOwnRed', "Amber, from this object's own confirmed detection")
    }
    if (hierarchyDiffers(p) && Number(p.redBelow || 0) > 0) {
        return t('objects.statusPair.amberFromBelow', 'Amber, from a dependency beneath')
    }
    return getAnalyticsConfig(rollupCode(p.hierarchy)).label
}

/** The plain-words explanation, one line per fact. The first line is always the object's own scan. */
export function describeStatusPair(p: StatusPair, t: Translate = english): string[] {
    const label = (code: number | null | undefined) => getAnalyticsConfig(code == null ? null : Number(code)).label
    const lines = [`${t('objects.statusPair.scan', 'Scan')}: ${label(p.scan)}`]
    lines.push(hierarchyDiffers(p)
        ? `${t('objects.statusPair.hierarchy', 'Hierarchy')}: ${hierarchyLabel(p, t)}`
        : `${t('objects.statusPair.hierarchy', 'Hierarchy')}: ${t('objects.statusPair.sameAsScan', 'Same as the scan')}`)
    const red = Number(p.redBelow || 0)
    const ind = Number(p.indicatorsBelow || 0)
    if (red > 0) {
        lines.push(t('objects.statusPair.redBelow', 'Confirmed detections beneath: {{count}}', { count: red }) + named(p.redBelowIds, red))
    }
    if (ind > 0) {
        lines.push(t('objects.statusPair.indicatorsBelow', 'Indicators beneath (not detections): {{count}}', { count: ind }) + named(p.indicatorsBelowIds, ind))
    }
    return lines
}
